// ============================================================
// SecaoCadastroFacial.js — termo de consentimento e foto de referência
//
// Tela pensada para uma SESSÃO ÚNICA de fotos, não para cadastro avulso: o
// coordenador percorre a lista do núcleo e resolve um atleta por vez, com o
// mesmo enquadramento e a mesma luz para todos. Foto de referência tirada
// assim aguenta selfie torta no registro; foto de festa recortada não.
//
// A trava do termo vive no servidor, não aqui. O botão desabilitado é
// cortesia com quem usa a tela, e não segurança.
// ============================================================

import { useState, useEffect, useCallback, useRef } from "react";
import {
  listarCadastroFacial, obterArquivoBiometrico, salvarConsentimentoRosto,
  salvarFotoReferencia, removerFotoReferencia, removerConsentimentoRosto,
  recusarConsentimentoRosto, estadoDoReconhecimento, carregarCadastroDoNucleo,
} from "../firebase";
import { AZUL, TEXTO, CINZA_ESC, BRANCO, VERDE, PERIGO, ALERTA, BORDA, BORDA_FORTE } from "../tema";
import { Card, Btn, StatusBox } from "../componentes";
import { gerarPDFTermo } from "../pdfRelatorio";
// O preparo da foto virou peça: a mesma redução serve ao retrato de
// uniforme do cadastro geral, e duas cópias divergiriam.
import { reduzirImagem, lerArquivoComoDataUrl } from "../imagem";
import { useNomeNucleo } from "../NucleosContexto";

function Etiqueta({ cor, fundo, children }) {
  return (
    <span style={{
      fontSize: 10.5, fontWeight: 700, padding: "2px 9px", borderRadius: 20,
      background: fundo, color: cor, whiteSpace: "nowrap",
    }}>{children}</span>
  );
}

// Em que pé está a conferência, em uma linha.
//
// Antes disto, saber se a ativação deu certo exigia cadastrar uma foto e
// observar o comportamento. Os quatro estados dizem coisas diferentes e
// pedem coisas diferentes de quem lê, então cada um tem o seu texto.
const ESTADO_DA_CREDENCIAL = {
  ativo: {
    cor: "#047857", fundo: "#DCFCE7", txt: "Credencial da AWS aceita",
    diz: "O serviço de reconhecimento respondeu. A conferência pode ser ligada.",
  },
  sem_credencial: {
    cor: "#92400E", fundo: "#FEF3C7", txt: "Sem credencial",
    diz: "A chave da AWS ainda não foi gravada. O cadastro de fotos funciona; a conferência, não.",
  },
  recusado: {
    cor: "#B91C1C", fundo: "#FEE2E2", txt: "Credencial recusada",
    diz: "A AWS respondeu que não reconhece esta chave. Confira se a chave de acesso e a secreta não foram trocadas uma pela outra.",
  },
  indisponivel: {
    cor: "#92400E", fundo: "#FEF3C7", txt: "Não deu para conferir agora",
    diz: "O serviço não respondeu nesta tentativa. Não quer dizer que a chave esteja errada.",
  },
};

function EstadoDaConferencia({ userNucleo }) {
  const [estado, setEstado] = useState(null);
  const [erro, setErro] = useState(false);

  useEffect(() => {
    let vivo = true;
    if (!userNucleo) return undefined;
    estadoDoReconhecimento(userNucleo)
      .then((r) => { if (vivo) setEstado(r); })
      .catch(() => { if (vivo) setErro(true); });
    return () => { vivo = false; };
  }, [userNucleo]);

  if (erro) return null;
  if (!estado) {
    return (
      <Card><div style={{ fontSize: 13, color: CINZA_ESC }}>Conferindo o estado do reconhecimento…</div></Card>
    );
  }

  const c = ESTADO_DA_CREDENCIAL[estado.credencial] || ESTADO_DA_CREDENCIAL.indisponivel;
  const faltamFotos = Math.max(0, (estado.autorizados || 0) - (estado.comFoto || 0));

  return (
    <Card>
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 8 }}>
        <div style={{ fontWeight: 700, fontSize: 15 }}>Estado do reconhecimento</div>
        <Etiqueta cor={c.cor} fundo={c.fundo}>{c.txt}</Etiqueta>
        <Etiqueta
          cor={estado.conferindo ? "#047857" : "#64748B"}
          fundo={estado.conferindo ? "#DCFCE7" : "#F1F5F9"}
        >
          {estado.conferindo ? "Conferência ligada" : "Conferência desligada"}
        </Etiqueta>
      </div>

      <div style={{ fontSize: 13, color: CINZA_ESC, lineHeight: 1.6 }}>{c.diz}</div>

      <div style={{ display: "flex", gap: 18, flexWrap: "wrap", marginTop: 12, fontSize: 13 }}>
        <span><b>{estado.atletas || 0}</b> atletas ativos</span>
        <span><b>{estado.autorizados || 0}</b> com autorização do responsável</span>
        <span><b>{estado.comFoto || 0}</b> com foto de referência</span>
      </div>

      {/* A ordem importa, e a tela diz qual é: sem foto cadastrada, ligar a
          conferência não muda nada, porque quem não tem foto é ignorado. */}
      {estado.credencial === "ativo" && !estado.conferindo && (
        <div style={{ fontSize: 12.5, color: CINZA_ESC, marginTop: 12, lineHeight: 1.6, borderTop: `1px solid ${BORDA}`, paddingTop: 10 }}>
          {faltamFotos > 0
            ? `Faltam ${faltamFotos} foto(s) de quem já tem autorização. Ligar a conferência antes disso não muda nada: quem não tem foto é ignorado.`
            : "Todo mundo que tem autorização já tem foto. A conferência pode ser ligada."}
        </div>
      )}
    </Card>
  );
}

function Instrucoes() {
  const itens = [
    "Rosto de frente, olhando para a câmera",
    "Sem boné, sem óculos escuros, sem máscara",
    "Luz na frente do rosto, nunca uma janela atrás",
    "Rosto ocupando boa parte do quadro, sem outras pessoas",
    "Expressão neutra, sem sorriso largo",
  ];
  return (
    <Card>
      <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 8 }}>Como tirar a foto de referência</div>
      <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13.5, color: CINZA_ESC, lineHeight: 1.75 }}>
        {itens.map((t) => <li key={t}>{t}</li>)}
      </ul>
      <div style={{ fontSize: 12.5, color: CINZA_ESC, marginTop: 10, lineHeight: 1.6 }}>
        A última parece exagero e não é: sorriso largo deforma as medidas do rosto e derruba a
        semelhança contra a selfie do registro, que sai sempre com cara séria.
      </div>
    </Card>
  );
}

// Os quatro estados do consentimento, cada um com a sua cor.
//
// "Pendente" não é uma falha nem uma recusa: é a família que ainda não
// devolveu o papel. Misturar os dois na mesma etiqueta cinza apagaria a
// diferença que a lei justamente cobra que exista.
const CORES_DO_ESTADO = {
  pendente: { txt: "Consentimento pendente", cor: "#92400E", fundo: "#FEF3C7" },
  autorizado: { txt: "✓ Autorizado", cor: "#047857", fundo: "#DCFCE7" },
  recusado: { txt: "Recusado pelo responsável", cor: "#B91C1C", fundo: "#FEE2E2" },
  revogado: { txt: "Autorização revogada", cor: "#B91C1C", fundo: "#FEE2E2" },
};

function EtiquetaDoEstado({ estado }) {
  const e = CORES_DO_ESTADO[estado] || CORES_DO_ESTADO.pendente;
  return <Etiqueta cor={e.cor} fundo={e.fundo}>{e.txt}</Etiqueta>;
}

export default function SecaoCadastroFacial({ userNucleo }) {
  const nomeNucleo = useNomeNucleo();

  // O termo sai daqui já com nome, nascimento, modalidade e núcleo
  // preenchidos. Quanto menos a coordenação copia do cadastro à mão,
  // menos chance de o termo assinado divergir do que está no sistema.
  //
  // A controladora é lida na hora do clique, e não guardada em estado:
  // quem acabou de cadastrá-la em Núcleos emite o termo em seguida, e
  // uma cópia em memória faria a recusa continuar aparecendo.
  async function baixarTermo(pessoa) {
    setErro(null);
    try {
      const cadastro = await carregarCadastroDoNucleo(userNucleo);
      await gerarPDFTermo({
        atleta: pessoa || null,
        nucleo: userNucleo,
        nucleoNome: nomeNucleo(userNucleo),
        instituicao: cadastro.instituicao,
        canalDeDireitos: cadastro.canalDeDireitos,
      });
    } catch (e) {
      setErro(e.message || "Não foi possível emitir o termo.");
    }
  }
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState(null);
  const [status, setStatus] = useState(null);
  const [ocupado, setOcupado] = useState(null);   // uid em processamento
  const [camera, setCamera] = useState(null);      // { uid, nome }
  const [visualizando, setVisualizando] = useState(null); // { nome, qual, url, tipo }

  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const recarregar = useCallback(async () => {
    setErro(null);
    try {
      setDados(await listarCadastroFacial(userNucleo));
    } catch (e) {
      setErro("Não foi possível carregar: " + (e.message || ""));
      setDados({ pessoas: [] });
    }
  }, [userNucleo]);

  useEffect(() => { recarregar(); }, [recarregar]);

  // A câmera fica ligada só enquanto a janela de captura está aberta. Sem
  // isto, a luz do aparelho fica acesa depois de fechar, o que assusta.
  const pararCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
  }, []);

  useEffect(() => {
    if (!camera) { pararCamera(); return undefined; }
    let cancelado = false;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "user", width: { ideal: 1280 } }, audio: false,
        });
        if (cancelado) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => null);
        }
      } catch {
        setStatus({ tipo: "erro", msg: "Não foi possível abrir a câmera. Permita o acesso ou use o envio de arquivo." });
        setCamera(null);
      }
    })();
    return () => { cancelado = true; pararCamera(); };
  }, [camera, pararCamera]);

  useEffect(() => pararCamera, [pararCamera]);

  async function enviarFoto(uid, dataUrl) {
    setOcupado(uid);
    setStatus(null);
    try {
      const reduzida = await reduzirImagem(dataUrl);
      const r = await salvarFotoReferencia({
        alunoUid: uid, fotoBase64: reduzida, tipoArquivo: "image/jpeg",
      });
      if (r && r.ok === false) {
        // Recusa por qualidade: a tela lista o que corrigir e a foto não é
        // gravada. Vale mais repetir agora do que descobrir no registro.
        setStatus({ tipo: "erro", msg: "Foto recusada:\n" + (r.problemas || []).join("\n") });
        return;
      }
      setStatus({ tipo: "ok", msg: "Foto de referência cadastrada." });
      setCamera(null);
      await recarregar();
    } catch (e) {
      setStatus({ tipo: "erro", msg: e.message || "Não foi possível salvar a foto." });
    } finally {
      setOcupado(null);
    }
  }

  function capturarDaCamera() {
    const video = videoRef.current;
    if (!video || !video.videoWidth) {
      setStatus({ tipo: "erro", msg: "A câmera ainda está carregando. Aguarde um instante." });
      return;
    }
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);
    enviarFoto(camera.uid, canvas.toDataURL("image/jpeg", 0.92));
  }

  async function escolherArquivo(uid, file, qual, estado) {
    if (!file) return;
    setOcupado(uid);
    setStatus(null);
    try {
      const dataUrl = await lerArquivoComoDataUrl(file);
      if (qual === "foto") {
        await enviarFoto(uid, dataUrl);
        return;
      }
      await salvarConsentimentoRosto({
        alunoUid: uid, arquivoBase64: dataUrl, tipoArquivo: file.type, estado,
      });
      setStatus({
        tipo: "ok",
        msg: estado === "autorizado"
          ? "Termo anexado, com autorização registrada."
          : "Termo anexado, com recusa registrada. A foto de referência não será cadastrada.",
      });
      await recarregar();
    } catch (e) {
      setStatus({ tipo: "erro", msg: e.message || "Não foi possível enviar." });
    } finally {
      setOcupado(null);
    }
  }

  // Recusa registrada sem arquivo. É o caso comum: o termo volta sem a
  // caixa marcada, ou a família avisa que não quer. Exigir um documento
  // de recusa faria a coordenação deixar o cadastro como "pendente" para
  // sempre, e some a diferença entre "não quis" e "ainda não respondeu".
  async function marcarRecusa(pessoa) {
    const aviso = pessoa.temFoto
      ? `Registrar que o responsável de ${pessoa.nome} NÃO autorizou?\n\nA foto de referência já cadastrada será apagada.`
      : `Registrar que o responsável de ${pessoa.nome} NÃO autorizou a verificação facial?`;
    if (!window.confirm(aviso)) return;

    setOcupado(pessoa.uid);
    setStatus(null);
    try {
      await recusarConsentimentoRosto({ alunoUid: pessoa.uid });
      setStatus({
        tipo: "ok",
        msg: "Recusa registrada. A presença continua sendo registrada normalmente, sem conferência facial.",
      });
      await recarregar();
    } catch (e) {
      setStatus({ tipo: "erro", msg: e.message || "Não foi possível registrar." });
    } finally {
      setOcupado(null);
    }
  }

  async function ver(pessoa, qual) {
    setOcupado(pessoa.uid);
    setStatus(null);
    try {
      const r = await obterArquivoBiometrico({ alunoUid: pessoa.uid, qual });
      setVisualizando({
        nome: pessoa.nome, qual, tipo: r.tipo,
        url: "data:" + r.tipo + ";base64," + r.base64,
      });
    } catch (e) {
      setStatus({ tipo: "erro", msg: e.message || "Não foi possível abrir." });
    } finally {
      setOcupado(null);
    }
  }

  async function remover(pessoa, qual) {
    const aviso = qual === "consentimento"
      ? `Retirar o consentimento de ${pessoa.nome}?\n\nO termo E a foto de referência serão apagados. O atleta continua registrando presença normalmente.`
      : `Apagar a foto de referência de ${pessoa.nome}?\n\nO termo continua anexado, então dá para tirar outra foto em seguida.`;
    if (!window.confirm(aviso)) return;

    setOcupado(pessoa.uid);
    setStatus(null);
    try {
      if (qual === "consentimento") await removerConsentimentoRosto(pessoa.uid);
      else await removerFotoReferencia(pessoa.uid);
      setStatus({ tipo: "ok", msg: "Removido." });
      await recarregar();
    } catch (e) {
      setStatus({ tipo: "erro", msg: e.message || "Não foi possível remover." });
    } finally {
      setOcupado(null);
    }
  }

  const pessoas = (dados && dados.pessoas) || [];
  const ativos = pessoas.filter((p) => p.ativo);
  const prontos = ativos.filter((p) => p.temFoto).length;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <Card>
        <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>Cadastro facial</div>
        <div style={{ fontSize: 13, color: CINZA_ESC, lineHeight: 1.6 }}>
          A foto de referência é comparada com a selfie do registro de presença. A conferência
          nunca bloqueia ninguém: quando o rosto não confere, a coordenação recebe um aviso e a
          presença continua valendo. Quem ainda não tem foto cadastrada registra presença
          normalmente, sem nenhuma diferença.
        </div>
        <div style={{ fontSize: 13, color: CINZA_ESC, marginTop: 10, lineHeight: 1.6 }}>
          Foto de rosto é dado pessoal sensível, e a maior parte dos atletas é menor de idade.
          Por isso o termo assinado precisa estar anexado antes da foto: pelo responsável legal,
          ou pelo próprio atleta quando ele já tiver 18 anos. O termo sai com a versão certa a
          partir da data de nascimento do cadastro.
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginTop: 14 }}>
          <Btn variant="secondary" onClick={() => baixarTermo(null)} style={{ fontSize: 13, padding: "8px 14px" }}>
            📄 Baixar termo em branco
          </Btn>
          <span style={{ fontSize: 12, color: CINZA_ESC, lineHeight: 1.5 }}>
            Na linha de cada atleta há o termo já preenchido com os dados dele. Imprima em duas
            vias, colha a assinatura do responsável e anexe aqui.
          </span>
        </div>
      </Card>

      <EstadoDaConferencia userNucleo={userNucleo} />

      <Instrucoes />

      {erro && <StatusBox tipo="erro" msg={erro} />}
      {status && <StatusBox tipo={status.tipo} msg={status.msg} />}

      <Card>
        <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
          <div style={{ fontWeight: 700, fontSize: 15 }}>Atletas do núcleo</div>
          {dados && (
            <span style={{ fontSize: 13, color: prontos === ativos.length ? VERDE : CINZA_ESC, fontWeight: 700 }}>
              {prontos} de {ativos.length} com foto cadastrada
            </span>
          )}
        </div>

        {!dados ? (
          <div style={{ fontSize: 13, color: CINZA_ESC }}>Carregando…</div>
        ) : ativos.length === 0 ? (
          <div style={{ padding: 14, background: "#F8FAFC", borderRadius: 8, fontSize: 13, color: CINZA_ESC, textAlign: "center" }}>
            Nenhum atleta ativo neste núcleo.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {ativos.map((p) => {
              const trabalhando = ocupado === p.uid;
              return (
                <div key={p.uid} style={{
                  border: "1px solid #E5E7EB", borderRadius: 10, padding: "11px 14px",
                  background: p.temFoto ? "#F6FBF4" : BRANCO,
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 9, flexWrap: "wrap" }}>
                    <span style={{ fontWeight: 700, fontSize: 14, color: TEXTO }}>{p.nome}</span>
                    <span style={{ fontSize: 12, color: CINZA_ESC }}>{p.modalidade}</span>

                    <EtiquetaDoEstado estado={p.estadoConsentimento} />
                    {p.temFoto
                      ? <Etiqueta cor="#047857" fundo="#DCFCE7">✓ Foto</Etiqueta>
                      : p.fotoApagadaNaSaida
                        ? <Etiqueta cor="#6D28D9" fundo="#EDE9FE">Foto apagada na saída</Etiqueta>
                        : <Etiqueta cor={CINZA_ESC} fundo="#F1F5F9">Sem foto</Etiqueta>}
                  </div>

                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10, alignItems: "center" }}>
                    <button onClick={() => baixarTermo(p)}
                      title="Termo preenchido com os dados deste atleta"
                      style={{
                        background: "none", border: `1px solid ${BORDA}`, borderRadius: 8,
                        padding: "6px 12px", fontSize: 12.5, fontWeight: 600, color: TEXTO,
                        cursor: "pointer", fontFamily: "inherit",
                      }}>
                      📄 Termo
                    </button>

                    {/* Duas entradas, uma para cada resposta do termo. O sistema
                        não deduz a autorização de ter recebido o papel: quem
                        anexa está com o documento na mão e diz o que foi
                        marcado. É aqui que consentimento presumido entraria. */}
                    {p.estadoConsentimento !== "autorizado" ? (
                      <>
                        <label style={{
                          background: ALERTA, color: BRANCO, borderRadius: 8, padding: "7px 13px",
                          fontSize: 12.5, fontWeight: 700, cursor: trabalhando ? "wait" : "pointer",
                        }}>
                          {trabalhando ? "Enviando…" : "📎 Anexar termo autorizado"}
                          <input type="file" accept="image/jpeg,image/png,application/pdf" disabled={trabalhando}
                            onChange={(e) => escolherArquivo(p.uid, e.target.files[0], "consentimento", "autorizado")}
                            style={{ display: "none" }} />
                        </label>
                        {/* Botão, e não anexo: não existe documento de recusa
                            para juntar. O termo impresso só tem a caixa de
                            AUTORIZO, e a recusa é o papel voltando sem marca
                            ou a família avisando que não quer. */}
                        <button onClick={() => marcarRecusa(p)} disabled={trabalhando}
                          title="Registrar que o responsável não autorizou"
                          style={{
                            border: `1.5px solid ${BORDA_FORTE}`, background: BRANCO, color: CINZA_ESC,
                            borderRadius: 8, padding: "6px 12px", fontSize: 12.5, fontWeight: 700,
                            cursor: trabalhando ? "wait" : "pointer", fontFamily: "inherit",
                          }}>
                          Termo Recusado
                        </button>
                      </>
                    ) : null}

                    {/* Antes o botão da foto simplesmente não existia enquanto
                        faltava o termo. Quem abria a tela não descobria a
                        ordem das coisas, descobria que faltava algo. Mostrar
                        desabilitado, com o motivo, ensina a sequência. */}
                    {p.estadoConsentimento !== "autorizado" ? (
                      <span title="A foto de referência só é cadastrada com autorização registrada" style={{
                        border: "1.5px dashed #CBD5E1", color: "#94A3B8", borderRadius: 8,
                        padding: "6px 12px", fontSize: 12.5, fontWeight: 700, cursor: "not-allowed",
                      }}>
                        📷 Tirar foto ({p.estadoConsentimento === "recusado" || p.estadoConsentimento === "revogado"
                          ? "sem autorização"
                          : "falta o termo"})
                      </span>
                    ) : (
                      <>
                        <Btn onClick={() => setCamera({ uid: p.uid, nome: p.nome })} disabled={trabalhando}
                          style={{ fontSize: 12.5, padding: "7px 13px" }}>
                          📷 {p.temFoto ? "Refazer foto" : "Tirar foto"}
                        </Btn>
                        <label style={{
                          border: `1.5px solid ${AZUL}`, color: AZUL, borderRadius: 8, padding: "6px 12px",
                          fontSize: 12.5, fontWeight: 700, cursor: trabalhando ? "wait" : "pointer",
                        }}>
                          📁 Enviar arquivo
                          <input type="file" accept="image/jpeg,image/png" disabled={trabalhando}
                            onChange={(e) => escolherArquivo(p.uid, e.target.files[0], "foto")}
                            style={{ display: "none" }} />
                        </label>
                        {p.temFoto && (
                          <button onClick={() => ver(p, "foto")} disabled={trabalhando}
                            style={{ background: "none", border: "none", color: AZUL, fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}>
                            Ver foto
                          </button>
                        )}
                        <button onClick={() => ver(p, "consentimento")} disabled={trabalhando}
                          style={{ background: "none", border: "none", color: CINZA_ESC, fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}>
                          Ver termo
                        </button>
                        {p.temFoto && (
                          <button onClick={() => remover(p, "foto")} disabled={trabalhando}
                            style={{ background: "none", border: "none", color: CINZA_ESC, fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}>
                            Apagar foto
                          </button>
                        )}
                        <button onClick={() => remover(p, "consentimento")} disabled={trabalhando}
                          style={{ background: "none", border: "none", color: PERIGO, fontSize: 12.5, fontWeight: 700, cursor: "pointer", marginLeft: "auto" }}>
                          Retirar consentimento
                        </button>
                      </>
                    )}
                  </div>

                  {!p.temFoto && p.fotoApagadaNaSaida && (
                    <div style={{ fontSize: 11.5, color: CINZA_ESC, marginTop: 8, lineHeight: 1.5 }}>
                      Teve foto cadastrada, apagada quando saiu do programa
                      {p.fotoApagadaEm ? " em " + new Date(p.fotoApagadaEm).toLocaleDateString("pt-BR") : ""}.
                      O termo assinado continua anexado. Para voltar a conferir, basta tirar outra foto.
                    </div>
                  )}

                  {p.temFoto && p.fotoPor && (
                    <div style={{ fontSize: 11.5, color: CINZA_ESC, marginTop: 8 }}>
                      Foto cadastrada por {p.fotoPor}
                      {p.fotoEm ? " em " + new Date(p.fotoEm).toLocaleDateString("pt-BR") : ""}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Captura pela câmera */}
      {camera && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(15,23,42,.72)", zIndex: 60,
          display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
        }}>
          <div style={{ background: BRANCO, borderRadius: 14, padding: 18, width: "min(560px, 100%)" }}>
            <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 2 }}>{camera.nome}</div>
            <div style={{ fontSize: 12.5, color: CINZA_ESC, marginBottom: 12 }}>
              Rosto de frente, sem boné, luz na frente. Enquadre o rosto no centro.
            </div>
            <video ref={videoRef} playsInline muted
              style={{ width: "100%", borderRadius: 10, background: "#0F172A", transform: "scaleX(-1)" }} />
            <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
              <Btn onClick={capturarDaCamera} disabled={ocupado === camera.uid}>
                {ocupado === camera.uid ? "Enviando…" : "📷 Capturar"}
              </Btn>
              <button onClick={() => setCamera(null)}
                style={{ background: BRANCO, color: CINZA_ESC, border: "1px solid #D1D5DB", borderRadius: 9, padding: "9px 16px", fontSize: 13.5, fontWeight: 700, cursor: "pointer" }}>
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Visualização do arquivo */}
      {visualizando && (
        <div onClick={() => setVisualizando(null)} style={{
          position: "fixed", inset: 0, background: "rgba(15,23,42,.72)", zIndex: 60,
          display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
        }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: BRANCO, borderRadius: 14, padding: 18, width: "min(680px, 100%)", maxHeight: "90vh", overflow: "auto" }}>
            <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 12 }}>
              {visualizando.nome} · {visualizando.qual === "foto" ? "foto de referência" : "termo de consentimento"}
            </div>
            {visualizando.tipo === "application/pdf" ? (
              <object data={visualizando.url} type="application/pdf" style={{ width: "100%", height: "65vh", borderRadius: 8 }}>
                <div style={{ fontSize: 13, color: CINZA_ESC }}>
                  O navegador não exibiu o PDF aqui. O arquivo está guardado corretamente.
                </div>
              </object>
            ) : (
              <img src={visualizando.url} alt="" style={{ width: "100%", borderRadius: 8 }} />
            )}
            <button onClick={() => setVisualizando(null)}
              style={{ marginTop: 14, background: BRANCO, color: CINZA_ESC, border: "1px solid #D1D5DB", borderRadius: 9, padding: "9px 16px", fontSize: 13.5, fontWeight: 700, cursor: "pointer" }}>
              Fechar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
